import { db } from '../firebase.config';
import {
  collection,
  getDocs,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
} from 'firebase/firestore';

const LOCAL_STORAGE_KEY = 'lhabilleur_coupons';

const DEFAULT_COUPONS = [
  {
    id: 'coupon-atelier10',
    code: 'ATELIER10',
    type: 'percentage', // 'percentage' | 'fixed'
    value: 10, // 10%
    minOrderAmount: 0,
    maxDiscount: 200,
    active: true,
    description: '10% Welcome gift across the entire Atelier Collection',
    usageCount: 42,
    createdAt: new Date().toISOString(),
  },
  {
    id: 'coupon-luxury50',
    code: 'LUXURY50',
    type: 'fixed',
    value: 50, // $50 off
    minOrderAmount: 300,
    maxDiscount: 50,
    active: true,
    description: '$50 VIP Reduction on orders exceeding $300',
    usageCount: 19,
    createdAt: new Date().toISOString(),
  },
  {
    id: 'coupon-royal20',
    code: 'ROYAL20',
    type: 'percentage',
    value: 20, // 20%
    minOrderAmount: 600,
    maxDiscount: 500,
    active: true,
    description: '20% Royal Connoisseur Privilege for orders above $600',
    usageCount: 8,
    createdAt: new Date().toISOString(),
  },
  {
    id: 'coupon-couture15',
    code: 'COUTURE15',
    type: 'percentage',
    value: 15,
    minOrderAmount: 250,
    maxDiscount: 150,
    active: true,
    description: '15% Haute Couture Privilege for seasonal patrons',
    usageCount: 31,
    createdAt: new Date().toISOString(),
  },
];

/**
 * Get all available coupons from Firestore or fallback to localStorage
 */
export const getCoupons = async () => {
  try {
    const querySnapshot = await getDocs(collection(db, 'coupons'));
    if (!querySnapshot.empty) {
      const list = [];
      querySnapshot.forEach((docSnap) => {
        list.push({ id: docSnap.id, ...docSnap.data() });
      });
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(list));
      return list;
    }
  } catch (error) {
    console.warn('Firestore coupons unavailable, reading from local cache:', error.message);
  }

  // Fallback to localStorage or default seed coupons
  const cached = localStorage.getItem(LOCAL_STORAGE_KEY);
  if (cached) {
    try {
      return JSON.parse(cached);
    } catch {
      // ignore
    }
  }

  localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(DEFAULT_COUPONS));
  return DEFAULT_COUPONS;
};

/**
 * Create a new promotional voucher
 */
export const createCoupon = async (couponData) => {
  const code = (couponData.code || '').trim().toUpperCase();
  if (!code) throw new Error('Voucher code is required');

  const newCoupon = {
    code,
    type: couponData.type || 'percentage',
    value: Number(couponData.value) || 10,
    minOrderAmount: Number(couponData.minOrderAmount) || 0,
    maxDiscount: Number(couponData.maxDiscount) || 9999,
    active: couponData.active !== false,
    description: couponData.description || `${code} VIP Privilege`,
    usageCount: 0,
    createdAt: new Date().toISOString(),
  };

  const id = `coupon-${code.toLowerCase()}-${Date.now().toString().slice(-4)}`;

  try {
    await setDoc(doc(db, 'coupons', id), {
      ...newCoupon,
      serverTime: serverTimestamp(),
    });
  } catch (err) {
    console.warn('Could not persist coupon to cloud, caching locally:', err.message);
  }

  // Update local cache
  const current = await getCoupons();
  const updated = [{ id, ...newCoupon }, ...current.filter((c) => c.code !== code)];
  localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(updated));
  window.dispatchEvent(new CustomEvent('lhabilleur_coupons_updated'));
  return { id, ...newCoupon };
};

/**
 * Toggle coupon status (active/paused)
 */
export const toggleCouponStatus = async (couponId, currentStatus) => {
  const newStatus = !currentStatus;
  try {
    const ref = doc(db, 'coupons', couponId);
    await updateDoc(ref, { active: newStatus });
  } catch (err) {
    console.warn('Could not toggle coupon in cloud:', err.message);
  }

  const current = await getCoupons();
  const updated = current.map((c) => (c.id === couponId ? { ...c, active: newStatus } : c));
  localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(updated));
  window.dispatchEvent(new CustomEvent('lhabilleur_coupons_updated'));
  return newStatus;
};

/**
 * Delete a coupon
 */
export const deleteCoupon = async (couponId) => {
  try {
    const ref = doc(db, 'coupons', couponId);
    await deleteDoc(ref);
  } catch (err) {
    console.warn('Could not delete coupon in cloud:', err.message);
  }

  const current = await getCoupons();
  const updated = current.filter((c) => c.id !== couponId);
  localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(updated));
  window.dispatchEvent(new CustomEvent('lhabilleur_coupons_updated'));
  return true;
};

/**
 * Validate a voucher code against an order subtotal
 */
export const validateCoupon = async (inputCode, orderTotal) => {
  if (!inputCode || !inputCode.trim()) {
    return { valid: false, message: 'Please enter a valid voucher code.' };
  }

  const cleanCode = inputCode.trim().toUpperCase();
  const coupons = await getCoupons();
  const matched = coupons.find((c) => c.code.toUpperCase() === cleanCode);

  if (!matched) {
    return {
      valid: false,
      message: `Privilege code "${cleanCode}" was not found or is invalid.`,
    };
  }

  if (!matched.active) {
    return {
      valid: false,
      message: `Privilege code "${cleanCode}" is currently inactive.`,
    };
  }

  if (matched.minOrderAmount && orderTotal < matched.minOrderAmount) {
    return {
      valid: false,
      message: `This code requires a minimum purchase of $${matched.minOrderAmount}. (Current total: $${orderTotal})`,
    };
  }

  let discountAmount = 0;
  if (matched.type === 'percentage') {
    discountAmount = Math.round((orderTotal * matched.value) / 100);
    if (matched.maxDiscount && discountAmount > matched.maxDiscount) {
      discountAmount = matched.maxDiscount;
    }
  } else {
    // fixed
    discountAmount = Math.min(matched.value, orderTotal);
  }

  return {
    valid: true,
    discountAmount,
    coupon: matched,
    message: `Privilege code "${matched.code}" applied! You save $${discountAmount}.`,
  };
};
